import type {
  CreateInstructionTaskInput,
  InstructRequest,
  InstructResponse,
  TaskFields,
  TaskType,
} from '@/components/session/note-instructions/noteInstructions.types';
import { DB_TYPE_TO_FRONTEND, REQUEST_DOCUMENT_TYPES } from '@/hooks/useTasks';
import { apiRequest } from '@/lib/queryClient';

const FRONTEND_TO_DB_TYPE = Object.fromEntries(
  Object.entries(DB_TYPE_TO_FRONTEND).map(([dbType, frontendType]) => [frontendType, dbType])
) as Record<TaskType, string>;

export const postNoteInstruction = async (body: InstructRequest): Promise<InstructResponse> =>
  (await apiRequest('POST', '/api/notes/instruct', body)).json();

/** Request tasks carry the generation marker RequestLetterSheet reads, like detected ones. */
export const instructionTaskExtraData = (type: TaskType, details: string): Record<string, unknown> => ({
  source: 'note_instruction',
  ...(REQUEST_DOCUMENT_TYPES.includes(type) && { request_document: { source_details: details, generated: false } }),
});

/** A request whose document is not generated yet takes its generation input from
 * `source_details`, so a details edit carries over; undefined when there is nothing to sync. */
export const requestSourceExtraData = (
  extraData: Record<string, unknown> | null | undefined,
  details: string
): Record<string, unknown> | undefined => {
  const requestDocument = extraData?.request_document;
  if (!requestDocument || typeof requestDocument !== 'object') return undefined;
  if ((requestDocument as { generated?: unknown }).generated !== false) return undefined;
  return { ...extraData, request_document: { ...requestDocument, source_details: details } };
};

/** Creates an unconfirmed (draft) task and returns its DB id. */
export const createInstructionTask = async ({
  type,
  title,
  details,
  sessionId,
  patientId,
  extraData,
}: CreateInstructionTaskInput) => {
  const response = await apiRequest('POST', '/api/tasks', {
    type: FRONTEND_TO_DB_TYPE[type] ?? type,
    title,
    description: details,
    session_id: sessionId,
    patient_id: patientId,
    extra_data: extraData,
  });
  const data: { todo_item?: { id?: unknown } } = await response.json();
  const id = data.todo_item?.id;
  if (typeof id !== 'number') throw new Error('Created task has no id');
  return id;
};

export const updateInstructionTask = async (
  dbId: number,
  { title, details }: TaskFields,
  extraData?: Record<string, unknown>
) => {
  await apiRequest('PATCH', `/api/tasks/${dbId}`, {
    title,
    description: details,
    ...(extraData && { extra_data: extraData }),
  });
};

export const deleteInstructionTask = async (dbId: number) => {
  await apiRequest('DELETE', `/api/tasks/${dbId}`);
};
